import { useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const fmt  = n => "$" + Math.round(n).toLocaleString("es-CL");
const pct  = n => parseFloat(n ?? 0).toFixed(1) + "%";
const clrM = v => (v >= 0 ? "#1D9E75" : "#E24B4A");

// Columnas del historial
const GRID = "1.2fr 0.45fr 0.45fr 0.45fr 0.45fr 0.55fr 0.6fr 0.5fr 1fr 1fr 1fr 1fr 1fr";

export default function AnalisisDiario({ datos }) {
  const enriched = useMemo(() => {
    if (!datos || datos.length === 0) return [];
    return [...datos]
      .sort((a, b) => (a.fecha > b.fecha ? 1 : -1))
      .map(d => ({
        ...d,
        tasa_confirmacion:
          d.total_pedidos > 0
            ? parseFloat(((d.confirmados / d.total_pedidos) * 100).toFixed(1))
            : 0,
        margen_real: Math.round(d.margen_real_actual ?? 0),
        margen_potencial: Math.round(
          (d.ingresos_potenciales ?? 0) - (d.salidas_fletes ?? 0) - (d.gasto_meta ?? 0)
        ),
        // Nuevos: sin descontar inversión Meta
        margen_producto: Math.round(
          (d.margen_real_actual ?? 0) + (d.gasto_meta ?? 0)
        ),
        margen_pot_sin_meta: Math.round(
          (d.ingresos_potenciales ?? 0) - (d.salidas_fletes ?? 0)
        ),
        fecha_corta: d.fecha?.slice(5, 10) ?? d.fecha,
      }));
  }, [datos]);

  const ultimo     = enriched[enriched.length - 1];
  const anteriores = [...enriched].reverse().slice(1);

  if (!ultimo)
    return (
      <div style={{ padding: "2rem", color: "#888", fontSize: 14 }}>
        Sin datos diarios disponibles.
      </div>
    );

  return (
    <div style={{ fontFamily: "system-ui, sans-serif" }}>

      {/* ── HERO: Último día ── */}
      <div style={{
        background: "#111", borderRadius: 14, padding: "1.5rem 1.75rem",
        marginBottom: "1.25rem", color: "#fff",
      }}>
        <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          Último día registrado
        </div>
        <div style={{ fontSize: 28, fontWeight: 600, marginBottom: "1.25rem" }}>
          {ultimo.fecha}
        </div>

        {/* KPIs generales */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: "1rem" }}>
          {[
            { label: "Pedidos",           value: ultimo.total_pedidos ?? 0, color: "#aaa",     plain: true },
            { label: "Ventas",            value: fmt(ultimo.ventas_ordenes ?? 0), color: "#1D9E75" },
            { label: "Tasa confirmación", value: pct(ultimo.tasa_confirmacion),   color: "#534AB7" },
            { label: "Tasa entrega",      value: pct(ultimo.tasa_entrega ?? 0),   color: "#4F81BD" },
          ].map(k => (
            <div key={k.label} style={{
              background: "rgba(255,255,255,0.07)", borderRadius: 10,
              padding: "12px 14px", borderTop: `3px solid ${k.color}`,
            }}>
              <div style={{ fontSize: 10, color: "#999", marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: k.color }}>
                {k.plain ? k.value.toLocaleString("es-CL") : k.value}
              </div>
            </div>
          ))}
        </div>

        {/* Márgenes: 2 grupos */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>

          {/* Grupo: Sin Meta */}
          <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid rgba(29,158,117,0.3)" }}>
            <div style={{ background: "rgba(29,158,117,0.15)", padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#1D9E75", display: "inline-block" }} />
              <span style={{ fontSize: 10, color: "#1D9E75", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Sin inversión Meta
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "rgba(29,158,117,0.1)" }}>
              {[
                { label: "Margen Producto",   value: fmt(ultimo.margen_producto),     color: clrM(ultimo.margen_producto)   },
                { label: "Margen Potencial",  value: fmt(ultimo.margen_pot_sin_meta), color: "#34C98A" },
              ].map(k => (
                <div key={k.label} style={{ background: "rgba(0,0,0,0.3)", padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: "#999", marginBottom: 6 }}>{k.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: k.color }}>{k.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Grupo: Después de Meta */}
          <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid rgba(239,159,39,0.35)" }}>
            <div style={{ background: "rgba(239,159,39,0.15)", padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#EF9F27", display: "inline-block" }} />
              <span style={{ fontSize: 10, color: "#EF9F27", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Después de financiar Meta Ads
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "rgba(239,159,39,0.1)" }}>
              {[
                { label: "Margen Real Actual", value: fmt(ultimo.margen_real),       color: clrM(ultimo.margen_real)  },
                { label: "Margen Potencial",   value: fmt(ultimo.margen_potencial),  color: "#F79646" },
              ].map(k => (
                <div key={k.label} style={{ background: "rgba(0,0,0,0.3)", padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: "#999", marginBottom: 6 }}>{k.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: k.color }}>{k.value}</div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Badges de estados */}
        <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
          {[
            { label: "Pendientes",  value: ultimo.pendientes  ?? 0, color: "#F79646" },
            { label: "Cancelados",  value: ultimo.cancelados  ?? 0, color: "#888"    },
            { label: "Confirmados", value: ultimo.confirmados ?? 0, color: "#534AB7" },
            { label: "En tránsito", value: ultimo.en_transito ?? 0, color: "#4F81BD" },
            { label: "Entregados",  value: ultimo.entregados  ?? 0, color: "#1D9E75" },
          ].map(k => (
            <div key={k.label} style={{ background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: "8px 12px", borderLeft: `3px solid ${k.color}` }}>
              <div style={{ fontSize: 10, color: "#777", marginBottom: 2 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── GRÁFICAS fila superior ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>

        {/* Ventas & Margen */}
        <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.85rem" }}>
            Ventas & Márgenes por día
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={enriched} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.15)" vertical={false} />
              <XAxis dataKey="fecha_corta" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: "#888" }} tickFormatter={v => "$" + Math.round(v / 1000) + "K"} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="ventas_ordenes"    name="Ventas"               fill="#1D9E75" opacity={0.8} radius={[3, 3, 0, 0]} />
              <Bar dataKey="ingresos_reales"   name="Cobrado billetera"    fill="#4F81BD" opacity={0.6} radius={[3, 3, 0, 0]} />
              <Line dataKey="margen_producto"  name="Margen s/Meta"  type="monotone" stroke="#34C98A"  strokeWidth={2} dot={{ r: 2 }} strokeDasharray="4 2" />
              <Line dataKey="margen_real"      name="Margen c/Meta"  type="monotone" stroke="#534AB7"  strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Tasas */}
        <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.85rem" }}>
            Tasa de Confirmación vs Entrega
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={enriched} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.15)" vertical={false} />
              <XAxis dataKey="fecha_corta" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: "#888" }} domain={[0, 100]} tickFormatter={v => v + "%"} />
              <Tooltip formatter={v => v.toFixed(1) + "%"} contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line dataKey="tasa_confirmacion" name="Confirmación %" type="monotone" stroke="#534AB7" strokeWidth={2} dot={{ r: 2, fill: "#534AB7" }} />
              <Line dataKey="tasa_entrega"      name="Entrega %"      type="monotone" stroke="#1D9E75" strokeWidth={2} dot={{ r: 2, fill: "#1D9E75" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Comparativa 4 márgenes */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem", marginBottom: "1rem" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.85rem" }}>
          Comparativa de márgenes por día
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
          {[
            { dot: "#34C98A",  label: "Margen Producto (sin Meta)",   dashed: true  },
            { dot: "#8FC9E0",  label: "Margen Potencial (sin Meta)",  dashed: true  },
            { dot: "#534AB7",  label: "Margen Real Actual (con Meta)" },
            { dot: "#F79646",  label: "Margen Potencial (con Meta)"   },
          ].map(l => (
            <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#888" }}>
              <span style={{ width: 20, height: 2, background: l.dot, display: "inline-block", borderTop: l.dashed ? `2px dashed ${l.dot}` : `2px solid ${l.dot}`, borderRadius: 1 }} />
              {l.label}
            </span>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={enriched} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.15)" vertical={false} />
            <XAxis dataKey="fecha_corta" tick={{ fontSize: 9, fill: "#888" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9, fill: "#888" }} tickFormatter={v => "$" + Math.round(v / 1000) + "K"} />
            <Tooltip formatter={v => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
            <Line dataKey="margen_producto"    name="Margen Producto s/Meta"   type="monotone" stroke="#34C98A" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="5 3" />
            <Line dataKey="margen_pot_sin_meta" name="Margen Pot. s/Meta"      type="monotone" stroke="#8FC9E0" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="5 3" />
            <Line dataKey="margen_real"        name="Margen Real c/Meta"       type="monotone" stroke="#534AB7" strokeWidth={2} dot={{ r: 2 }} />
            <Line dataKey="margen_potencial"   name="Margen Potencial c/Meta"  type="monotone" stroke="#F79646" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── HISTORIAL ── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem", overflowX: "auto" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>
          Historial diario
        </div>

        <div style={{ minWidth: 900 }}>

          {/* Fila 1: grupos de columnas */}
          <div style={{ display: "grid", gridTemplateColumns: GRID }}>
            <div style={{ gridColumn: "span 9", padding: "4px 6px" }} />
            {/* Sin Meta */}
            <div style={{
              gridColumn: "span 2", fontSize: 9, fontWeight: 700,
              color: "#1D9E75", textTransform: "uppercase", letterSpacing: "0.05em",
              padding: "4px 6px", background: "rgba(29,158,117,0.06)",
              borderRadius: "6px 6px 0 0", borderTop: `2px solid #1D9E75`,
              textAlign: "center",
            }}>
              Sin Meta
            </div>
            {/* Con Meta */}
            <div style={{
              gridColumn: "span 2", fontSize: 9, fontWeight: 700,
              color: "#EF9F27", textTransform: "uppercase", letterSpacing: "0.05em",
              padding: "4px 6px", background: "rgba(239,159,39,0.07)",
              borderRadius: "6px 6px 0 0", borderTop: `2px solid #EF9F27`,
              textAlign: "center", marginLeft: 3,
            }}>
              Después de Meta
            </div>
          </div>

          {/* Fila 2: cabeceras de columnas */}
          <div style={{ display: "grid", gridTemplateColumns: GRID, borderBottom: "0.5px solid #eee" }}>
            {[
              { label: "Fecha",       span: 1 },
              { label: "Total",       span: 1 },
              { label: "Pend.",       span: 1 },
              { label: "Canc.",       span: 1 },
              { label: "Conf.",       span: 1 },
              { label: "Tránsito",    span: 1 },
              { label: "Entregados",  span: 1 },
              { label: "Fallidos",    span: 1 },
              { label: "Ventas",      span: 1 },
            ].map(h => (
              <div key={h.label} style={{ fontSize: 10, color: "#aaa", padding: "5px 6px", fontWeight: 500 }}>{h.label}</div>
            ))}
            {/* Cabeceras Sin Meta */}
            {["Marg. Producto", "Marg. Potencial"].map(h => (
              <div key={h} style={{ fontSize: 10, color: "#1D9E75", padding: "5px 6px", fontWeight: 600, background: "rgba(29,158,117,0.05)", textAlign: "center" }}>{h}</div>
            ))}
            {/* Cabeceras Con Meta */}
            {["Marg. Real", "Marg. Potencial"].map(h => (
              <div key={h + "_meta"} style={{ fontSize: 10, color: "#EF9F27", padding: "5px 6px", fontWeight: 600, background: "rgba(239,159,39,0.05)", textAlign: "center", marginLeft: h === "Marg. Real" ? 3 : 0 }}>{h}</div>
            ))}
          </div>

          {/* Filas de datos */}
          {anteriores.map((d, i) => (
            <div
              key={d.fecha}
              style={{
                display: "grid",
                gridTemplateColumns: GRID,
                borderBottom: i < anteriores.length - 1 ? "0.5px solid #f5f5f4" : "none",
                background: i % 2 === 0 ? "transparent" : "#fafaf9",
              }}
            >
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#555" }}>{d.fecha}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", fontWeight: 600, color: "#111" }}>{d.total_pedidos ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#F79646" }}>{d.pendientes  ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#888"    }}>{d.cancelados  ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#534AB7" }}>{d.confirmados ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#4F81BD" }}>{d.en_transito ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#1D9E75" }}>{d.entregados  ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#E24B4A" }}>{d.fallidos    ?? 0}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", fontWeight: 500  }}>{fmt(d.ventas_ordenes ?? 0)}</div>
              {/* Sin Meta */}
              <div style={{ fontSize: 12, padding: "9px 6px", fontWeight: 600, color: clrM(d.margen_producto), background: "rgba(29,158,117,0.04)", textAlign: "center" }}>{fmt(d.margen_producto)}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", fontWeight: 500, color: "#2DB37B",               background: "rgba(29,158,117,0.04)", textAlign: "center" }}>{fmt(d.margen_pot_sin_meta)}</div>
              {/* Con Meta */}
              <div style={{ fontSize: 12, padding: "9px 6px", fontWeight: 600, color: clrM(d.margen_real),   background: "rgba(239,159,39,0.04)",  textAlign: "center", marginLeft: 3 }}>{fmt(d.margen_real)}</div>
              <div style={{ fontSize: 12, padding: "9px 6px", color: "#F79646",                               background: "rgba(239,159,39,0.04)",  textAlign: "center" }}>{fmt(d.margen_potencial)}</div>
            </div>
          ))}

          {anteriores.length === 0 && (
            <div style={{ fontSize: 12, color: "#aaa", textAlign: "center", padding: "1.5rem 0" }}>
              Solo hay un día registrado.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
